import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { EmbersBackdrop } from "../components/site/Atoms";
import SEO, { ORG_SCHEMA, breadcrumbSchema, faqSchema } from "../components/SEO";
import SocialFollow from "../components/SocialFollow";
import CounselingDisclaimer from "../components/CounselingDisclaimer";
import { findLandingPage, SEO_LANDING_PAGES } from "../data/seoLandingPages";

const RelatedCard = ({ page }) => (
  <Link
    to={page.path}
    className="group bento-card !p-6 flex flex-col gap-3 hover:-translate-y-0.5 transition-all"
    data-testid={`related-${page.path.replace(/\//g, "")}`}
  >
    <span className="glow-blob" />
    <div className="overline">{page.kicker}</div>
    <div className="font-serif-display text-xl text-[#F6F0E6] leading-tight">{page.h1}</div>
    <div className="mt-auto text-[#C9A44C] text-xs inline-flex items-center gap-1 group-hover:gap-2 transition-all">
      Read more <ChevronRight className="h-3 w-3" />
    </div>
  </Link>
);

const SeoLanding = ({ path }) => {
  const page = findLandingPage(path);
  if (!page) return null;

  const related = (page.relatedSlugs || [])
    .map((p) => SEO_LANDING_PAGES.find((x) => x.path === p))
    .filter(Boolean);

  const schemas = [
    ORG_SCHEMA,
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: page.kicker || page.h1, path: page.path },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": page.title,
      "description": page.description,
      "url": `https://revivalfireministries.com${page.path}`,
      "isPartOf": { "@type": "WebSite", "name": "Revival Fire Ministries Global", "url": "https://revivalfireministries.com" },
    },
    ...(page.faqs?.length ? [faqSchema(page.faqs)] : []),
  ];

  return (
    <>
      <SEO
        title={page.title}
        description={page.description}
        path={page.path}
        jsonLd={schemas}
      />
      <main className="relative" data-testid={`seo-landing-${page.path.replace(/\//g, "") || "root"}`}>
        <Navbar />

        {/* HERO */}
        <section className="relative pt-28 pb-20 sm:pt-32 sm:pb-24 px-6 overflow-hidden">
          <EmbersBackdrop density={10} />
          <div className="relative max-w-6xl mx-auto">
            <nav className="text-xs tracking-[0.22em] uppercase text-[#D9D9D9]/55 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#C9A44C]">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-[#C9A44C]">{page.kicker}</span>
            </nav>
            <div className="overline mb-4">{page.kicker}</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#F6F0E6] leading-[1.05] max-w-4xl">
              {page.h1}
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-[#D9D9D9] leading-relaxed">{page.intro}</p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to={page.cta.primary.href} className="btn-gold !py-3 !px-6 !text-sm" data-testid="seo-cta-primary">
                {page.cta.primary.label} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to={page.cta.secondary.href} className="btn-ghost !py-3 !px-6 !text-sm" data-testid="seo-cta-secondary">
                {page.cta.secondary.label}
              </Link>
            </div>
          </div>
        </section>

        {/* ANSWER BLOCK — high-value for AI engines */}
        <section className="px-6 -mt-8" aria-label="Direct answer">
          <div className="max-w-4xl mx-auto bento-card !p-8 sm:!p-10 border-l-4 border-[#C9A44C]">
            <span className="glow-blob" />
            <div className="overline mb-3">In short</div>
            <p className="text-[#F6F0E6] text-lg leading-relaxed">{page.answer}</p>
          </div>
        </section>

        {/* BODY SECTIONS */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto space-y-12">
            {page.sections.map((s, i) => (
              <article key={i}>
                <h2 className="font-serif-display text-3xl text-[#F6F0E6] mb-4">{s.heading}</h2>
                {s.body.split("\n\n").map((p, j) => (
                  <p key={j} className="text-[#D9D9D9] leading-relaxed mb-3">{p}</p>
                ))}
              </article>
            ))}

            {page.scriptures?.length > 0 && (
              <article>
                <h2 className="font-serif-display text-3xl text-[#F6F0E6] mb-5">Biblical foundation</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {page.scriptures.map((sc, i) => (
                    <blockquote key={i} className="bento-card !p-6">
                      <span className="glow-blob" />
                      <div className="text-[#C9A44C] text-xs tracking-[0.22em] uppercase mb-2">{sc.ref}</div>
                      <p className="text-[#D9D9D9] italic leading-relaxed">&ldquo;{sc.text}&rdquo;</p>
                    </blockquote>
                  ))}
                </div>
              </article>
            )}

            {page.whoFor?.length > 0 && (
              <article>
                <h2 className="font-serif-display text-3xl text-[#F6F0E6] mb-4">Who this is for</h2>
                <ul className="space-y-2.5">
                  {page.whoFor.map((w, i) => (
                    <li key={i} className="flex gap-3 text-[#D9D9D9]">
                      <span className="text-[#C9A44C] mt-1.5">▸</span>
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </article>
            )}

            {page.whatToExpect?.length > 0 && (
              <article>
                <h2 className="font-serif-display text-3xl text-[#F6F0E6] mb-4">What to expect</h2>
                <ol className="space-y-2.5 list-decimal list-inside marker:text-[#C9A44C]">
                  {page.whatToExpect.map((w, i) => (
                    <li key={i} className="text-[#D9D9D9]">{w}</li>
                  ))}
                </ol>
              </article>
            )}

            {page.counseling && <CounselingDisclaimer />}

            {page.faqs?.length > 0 && (
              <article>
                <h2 className="font-serif-display text-3xl text-[#F6F0E6] mb-5">Frequently asked questions</h2>
                <div className="space-y-3">
                  {page.faqs.map((f, i) => (
                    <details key={i} className="bento-card !p-5 group">
                      <span className="glow-blob" />
                      <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-[#F6F0E6]">
                        <span className="font-medium">{f.q}</span>
                        <ChevronRight className="h-4 w-4 text-[#C9A44C] group-open:rotate-90 transition-transform" />
                      </summary>
                      <p className="mt-3 text-[#D9D9D9] leading-relaxed">{f.a}</p>
                    </details>
                  ))}
                </div>
              </article>
            )}
          </div>
        </section>

        {/* RELATED */}
        {related.length > 0 && (
          <section className="py-16 px-6 border-t border-[#C9A44C]/10">
            <div className="max-w-6xl mx-auto">
              <div className="overline mb-4">Continue exploring</div>
              <h2 className="font-serif-display text-3xl text-[#F6F0E6] mb-8">Related ministry</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {related.map((r) => <RelatedCard key={r.path} page={r} />)}
              </div>
            </div>
          </section>
        )}

        <SocialFollow />

        {/* FINAL CTA */}
        <section className="py-20 px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#F6F0E6]">
              Come and meet Jesus. Carry the <span className="italic gold-text">fire</span>.
            </h2>
            <p className="mt-4 text-[#D9D9D9]">Be trained. Be equipped. Be sent.</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link to={page.cta.primary.href} className="btn-gold !py-3 !px-6 !text-sm">
                {page.cta.primary.label} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to={page.cta.secondary.href} className="btn-ghost !py-3 !px-6 !text-sm">
                {page.cta.secondary.label}
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
};

export default SeoLanding;
